import { useState } from 'react';
import { ChevronDownIcon, InfoIcon } from 'lucide-react';

const VARIABLE_GROUPS = [
  {
    label: 'Basic',
    variables: [
      { name: '{{$uuid}}', description: 'Random UUID' },
      { name: '{{$randomInt}}', description: 'Random integer (0-1000)' },
      { name: '{{$randomFloat}}', description: 'Random float (0.00-1.00)' },
      { name: '{{$randomBool}}', description: 'true or false' },
      { name: '{{$randomEmail}}', description: 'Random email address' },
      { name: '{{$randomName}}', description: 'Random full name' },
      { name: '{{$randomString}}', description: 'Random 16-char string' },
      { name: '{{$timestamp}}', description: 'ISO 8601 timestamp' },
      { name: '{{$timestampUnix}}', description: 'Unix timestamp (seconds)' },
      { name: '{{$date}}', description: 'Date (YYYY-MM-DD)' },
    ],
  },
  {
    label: 'Identity & Contact',
    variables: [
      { name: '{{$firstName}}', description: 'First name' },
      { name: '{{$lastName}}', description: 'Last name' },
      { name: '{{$phone}}', description: 'Phone number' },
      { name: '{{$username}}', description: 'Username (e.g. alice.smith42)' },
      { name: '{{$avatar}}', description: 'Avatar image URL' },
    ],
  },
  {
    label: 'Location',
    variables: [
      { name: '{{$street}}', description: 'Street address' },
      { name: '{{$city}}', description: 'City name' },
      { name: '{{$state}}', description: 'US state' },
      { name: '{{$zip}}', description: 'ZIP code' },
      { name: '{{$country}}', description: 'Country name' },
      { name: '{{$latitude}}', description: 'Latitude (-90 to 90)' },
      { name: '{{$longitude}}', description: 'Longitude (-180 to 180)' },
    ],
  },
  {
    label: 'Text',
    variables: [
      { name: '{{$word}}', description: 'Single word' },
      { name: '{{$sentence}}', description: 'Full sentence' },
      { name: '{{$paragraph}}', description: 'Multi-sentence paragraph' },
      { name: '{{$slug}}', description: 'URL-friendly slug' },
    ],
  },
  {
    label: 'Date & Time',
    variables: [
      { name: '{{$dateISO}}', description: 'Current ISO date' },
      { name: '{{$datePast}}', description: 'Random past date (within 1 year)' },
      { name: '{{$dateFuture}}', description: 'Random future date (within 1 year)' },
      { name: '{{$time}}', description: 'Time of day (HH:MM:SS)' },
    ],
  },
  {
    label: 'Internet',
    variables: [
      { name: '{{$url}}', description: 'Full URL' },
      { name: '{{$domain}}', description: 'Domain name' },
      { name: '{{$ipv4}}', description: 'IPv4 address' },
      { name: '{{$ipv6}}', description: 'IPv6 address' },
      { name: '{{$color}}', description: 'Hex color (e.g. #a3c1f0)' },
      { name: '{{$userAgent}}', description: 'Browser user agent string' },
    ],
  },
  {
    label: 'Parameterized',
    variables: [
      { name: '{{$randomInt:min:max}}', description: 'Integer in custom range' },
      { name: '{{$randomFloat:min:max:dec}}', description: 'Float with range & precision' },
      { name: '{{$randomString:len}}', description: 'String of custom length' },
      { name: '{{$oneOf:a,b,c}}', description: 'Pick from comma-separated list' },
    ],
  },
  {
    label: 'Array Generation',
    variables: [
      { name: '{{#repeat:N}}...{{/repeat}}', description: 'Repeat block N times (comma-separated)' },
      { name: '{{$index}}', description: '0-based iteration index (inside repeat)' },
    ],
  },
  {
    label: 'Request Data',
    variables: [
      { name: '{{request.method}}', description: 'HTTP method (GET, POST, ...)' },
      { name: '{{request.path}}', description: 'Request path' },
      { name: '{{request.header.Name}}', description: 'Header value by name' },
      { name: '{{request.query.key}}', description: 'Query parameter value' },
      { name: '{{request.body}}', description: 'Raw request body' },
      { name: '{{request.body.field}}', description: 'JSON body field (dot notation)' },
    ],
  },
  {
    label: 'Variables',
    variables: [
      { name: '{{vars.name}}', description: 'Endpoint-level variable' },
      { name: '{{project.name}}', description: 'Project-level variable' },
      { name: '{{globals.name}}', description: 'Account-level variable' },
    ],
  },
  {
    label: 'Path Parameters',
    variables: [
      { name: '{{paramName}}', description: 'Path parameter from :paramName' },
    ],
  },
];

export function TemplateVariableRef() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1"
      >
        <InfoIcon className="size-3.5" />
        {isOpen ? 'Hide' : 'Show'} template variables
        <ChevronDownIcon className={`size-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="mt-2 p-3 bg-base-200/50 rounded-box text-xs space-y-3">
          <p className="text-base-content/80">
            Use <code className="bg-base-300 px-1 rounded-field">{'{{variable}}'}</code> in your response body to insert dynamic values at request time.
          </p>
          {VARIABLE_GROUPS.map((group) => (
            <div key={group.label}>
              <h4 className="font-semibold text-base-content/80 mb-1">{group.label}</h4>
              <div className="grid grid-cols-1 gap-0.5">
                {group.variables.map((v) => (
                  <div key={v.name} className="flex items-baseline gap-2">
                    <code className="text-primary bg-base-300 px-1 rounded-field whitespace-nowrap font-mono">{v.name}</code>
                    <span className="text-base-content/70">{v.description}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-base-content/70 italic">
            Example: <code className="bg-base-300 px-1 rounded-field">{'{"id": "{{$uuid}}", "method": "{{request.method}}"}'}</code>
          </p>
        </div>
      )}
    </div>
  );
}
